import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MEMBER_COLORS,
  PING_TTL_MS,
  type Pin,
  type PinKind,
  type StrokePoint,
} from "@tarkov-callout/shared";
import { NotInDiscord, bootstrap, inviteCodeFromUrl, type AppSession } from "./bootstrap";
import { describeError, t } from "./i18n";
import { EXTRACT_FACTIONS, getMap, mapTitle, type ExtractFaction } from "./maps";
import { MapCanvas, type MapStyle, type Placement } from "./map/MapCanvas";
import { PinCard } from "./components/PinCard";
import { InviteJoin } from "./components/InviteJoin";
import { Changelog } from "./components/Changelog";
import { Landing } from "./components/Landing";
import { Sidebar } from "./components/Sidebar";
import { Toolbar, type Tool } from "./components/Toolbar";
import { useCompact } from "./useCompact";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { describe, report, setReportContext } from "./report";
import { useRoom } from "./useRoom";
import { startUsage, used } from "./usage";

export function App() {
  const [session, setSession] = useState<AppSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [landing, setLanding] = useState<string | null>(null);
  // Read once: redeeming scrubs the path, and we must not lose the code.
  const [inviteCode] = useState(inviteCodeFromUrl);

  useEffect(() => {
    if (inviteCode) return;
    if (window.location.pathname === "/changelog") return;
    bootstrap()
      .then(setSession)
      .catch((cause: unknown) => {
        if (cause instanceof NotInDiscord) {
          setLanding(cause.installUrl);
          return;
        }
        /*
         * What the log needs and what the person needs are not the same thing.
         *
         * The Discord SDK rejects with a bare `{code, message}` object, so
         * `String(cause)` here used to send us "[object Object]" and no stack --
         * and then put that same string on screen. `describe` keeps the report
         * readable; `describeError` keeps the screen readable, preferring a
         * translated message key, then the server's own sentence.
         */
        const described = describe(cause);
        report({ where: "bootstrap", ...described }, "boot");
        setError(describeError(cause, "boot.failedUnknown"));
      });
  }, [inviteCode]);

  if (inviteCode && !session) {
    return <InviteJoin code={inviteCode} onJoined={setSession} />;
  }

  if (window.location.pathname === "/changelog") {
    return <Changelog />;
  }

  if (landing !== null) {
    return <Landing installUrl={landing} />;
  }

  if (error) {
    return (
      <div className="boot boot--error">
        <h1>{t("boot.failedTitle")}</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="boot">
        <div className="boot__spinner" />
        <p>{t("boot.connecting")}</p>
      </div>
    );
  }

  return (
    // Re-wrapped now that the trace id exists, so a crash inside the room shows
    // the code the person needs to quote.
    <ErrorBoundary trace={session.trace}>
      <Room session={session} />
    </ErrorBoundary>
  );
}

function Room({ session }: { session: AppSession }) {
  const room = useRoom(session);
  const map = getMap(room.mapId);

  // Keeps crash reports tied to the room and map they happened on.
  useEffect(() => {
    setReportContext({
      session: session.session,
      room: session.instanceId,
      platform: session.pip?.platform ?? (session.inDiscord ? "discord" : "browser"),
      mapId: room.mapId,
    });
  }, [session, room.mapId]);

  const [preferredStyle, setPreferredStyle] = useState<MapStyle>("svg");
  const [activeLayer, setActiveLayer] = useState<string | null>(null);
  const [tool, setTool] = useState<Tool | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Where the map has been asked to look, and how many times it has been asked. */
  const [focus, setFocus] = useState<{ lat: number; lng: number; seq: number } | null>(null);
  const focusSeq = useRef(0);
  /** Only meaningful in the compact layout; ignored when the sidebar fits. */
  const [panelOpen, setPanelOpen] = useState(false);
  /*
   * The opposite control, for windows the sidebar does fit in: put the map on
   * its own. Asked for twice ("I want to show only the map"), and it is also
   * the only thing we can offer the people whose Activity window has gone
   * small -- 264px of panel is a third of it.
   */
  const [sidebarHidden, setSidebarHidden] = useState(false);
  // Per viewer, like the floor and style toggles. Off until asked for.
  const [showExtracts, setShowExtracts] = useState(false);
  /** Same rule as the extracts: reference data, off until someone asks. */
  const [showSpawns, setShowSpawns] = useState(false);
  /*
   * Which kinds are drawn. All of them until someone narrows it: a Scav being
   * walked to an exit does not need the seven PMC ones on screen, and the
   * person teaching is the one who knows which is which.
   */
  const [factions, setFactions] = useState<ExtractFaction[]>(EXTRACT_FACTIONS);
  /*
   * Which colour the pen draws in. null means "whatever colour the room gave
   * me", so it follows the roster instead of freezing whatever was known
   * before the roster arrived.
   */
  const [penChoice, setPenChoice] = useState<string | null>(null);
  const compact = useCompact();

  // The session is what the counters are grouped by; nothing is sent before it.
  useEffect(() => {
    startUsage(session.session);
  }, [session.session]);

  // Worth knowing on its own: how many people are on a window this small.
  useEffect(() => {
    if (compact) used("compact");
  }, [compact]);

  /** Not every map ships both renderings, so fall back to whatever exists. */
  const style: MapStyle =
    preferredStyle === "tile" && map.tilePath ? "tile" : map.svgPath ? "svg" : "tile";

  // A floor from the previous map means nothing on the new one.
  useEffect(() => {
    setActiveLayer(null);
    setSelectedId(null);
    setFocus(null);
  }, [map.id]);

  const selected = useMemo(
    () => room.pins.find((pin) => pin.id === selectedId) ?? null,
    [room.pins, selectedId],
  );

  /** Falls back until the roster arrives; the server picks the real colour. */
  const selfColor = useMemo(
    () => room.members.find((member) => member.id === room.selfId)?.color ?? MEMBER_COLORS[0],
    [room.members, room.selfId],
  );

  const penColor = penChoice ?? selfColor;

  /** Set when we place a pin, so we can select it once the server echoes it. */
  const awaitingPinRef = useRef<number | null>(null);

  useEffect(() => {
    const placedAt = awaitingPinRef.current;
    if (placedAt === null || !room.selfId) return;

    const mine = room.pins
      .filter((pin) => pin.authorId === room.selfId && pin.createdAt >= placedAt - 5_000)
      .sort((a, b) => b.createdAt - a.createdAt)[0];

    if (mine) {
      awaitingPinRef.current = null;
      setSelectedId(mine.id);
    }
  }, [room.pins, room.selfId]);

  const handlePlace = useCallback(
    ({ lat, lng, heading }: Placement) => {
      if (!tool || tool === "eraser") return;
      const isPing = tool === "ping";
      const kind: PinKind = isPing ? "objective" : tool;

      if (!isPing) awaitingPinRef.current = Date.now();
      room.addPin({
        mapId: map.id,
        layer: activeLayer,
        lat,
        lng,
        heading,
        kind,
        label: "",
        ttlMs: isPing ? PING_TTL_MS : null,
      });
      setTool(null);
    },
    [tool, room, map.id, activeLayer],
  );

  /**
   * A stroke arrives finished, so it is sent whole on release.
   *
   * The pen stays armed afterwards: drawing a route is several strokes, and
   * having to re-arm between each one is the difference between a pen and a
   * button you press before every line.
   */
  const handleDraw = useCallback(
    (path: StrokePoint[]) => {
      const first = path[0];
      if (!first) return;
      room.addPin({
        mapId: map.id,
        layer: activeLayer,
        lat: first[0],
        lng: first[1],
        heading: null,
        kind: "draw",
        label: "",
        path,
        color: penColor,
      });
    },
    [room, map.id, activeLayer, penColor],
  );

  /*
   * The rubber does not open anything or ask anything: what it touches goes.
   * The server is the one that says no -- somebody else's own marker stays.
   */
  const handleErase = useCallback(
    (id: string) => {
      room.removePin(id, "eraser");
      setSelectedId((current) => (current === id ? null : current));
    },
    [room],
  );

  const handleSelectPin = useCallback((pin: Pin) => {
    setSelectedId(pin.id);
  }, []);

  /*
   * From the list, "select" also means "show me". Picking something you cannot
   * see and having nothing move would be the same as the list not working.
   */
  const handleSelectFromList = useCallback((pin: Pin) => {
    setSelectedId(pin.id);
    focusSeq.current += 1;
    setFocus({ lat: pin.lat, lng: pin.lng, seq: focusSeq.current });
  }, []);

  const handleRemoveFromList = useCallback(
    (id: string) => {
      room.removePin(id, "list");
      setSelectedId((current) => (current === id ? null : current));
    },
    [room],
  );

  const handleMovePin = useCallback(
    (id: string, lat: number, lng: number) => {
      room.movePin(id, lat, lng);
    },
    [room],
  );

  // Keyboard: number keys arm a tool, Escape disarms, Delete removes the selection.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.isContentEditable)) return;

      /*
       * Undo is only ever the last line *you* drew. A freehand stroke is the
       * one thing here that comes out wrong often enough to need taking back
       * in a hurry, and reaching for the card to delete it breaks the drawing.
       */
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        const mine = room.pins
          .filter((pin) => pin.kind === "draw" && pin.authorId === room.selfId)
          .sort((a, b) => b.createdAt - a.createdAt)[0];
        if (mine) {
          event.preventDefault();
          room.removePin(mine.id, "undo");
        }
        return;
      }

      if (event.key === "Escape") {
        setTool(null);
        setSelectedId(null);
        setPanelOpen(false);
        return;
      }
      if ((event.key === "Delete" || event.key === "Backspace") && selectedId) {
        room.removePin(selectedId, "key");
        setSelectedId(null);
        return;
      }
      const order: Tool[] = ["objective", "rally", "enemy", "loot", "extract", "danger"];
      const index = Number(event.key) - 1;
      if (Number.isInteger(index) && index >= 0 && index < order.length) {
        setTool(order[index] ?? null);
      }
      if (event.key.toLowerCase() === "q") setTool("ping");
      if (event.key.toLowerCase() === "e") setTool("self");
      if (event.key.toLowerCase() === "d") setTool((current) => (current === "draw" ? null : "draw"));
      if (event.key.toLowerCase() === "x") setTool((current) => (current === "eraser" ? null : "eraser"));
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [room, selectedId]);

  return (
    <div
      className={`app${panelOpen ? " app--panel-open" : ""}${
        sidebarHidden ? " app--sidebar-hidden" : ""
      }`}
    >
      {/*
        In a picture-in-picture window there is no room for a 264px column, so
        the sidebar becomes an overlay that this button reveals. Driven by a
        media query rather than a layout event: the viewport size is the thing
        that actually decides whether the sidebar fits, and it cannot fall out
        of sync the way a cached mode flag can.
      */}
      <button
        type="button"
        className="panel-toggle"
        aria-label="Menu"
        aria-expanded={panelOpen}
        onClick={() => setPanelOpen((open) => !open)}
      >
        {panelOpen ? "✕" : "☰"}
      </button>

      <Sidebar
        room={room}
        map={map}
        style={style}
        onStyleChange={setPreferredStyle}
        activeLayer={activeLayer}
        onLayerChange={setActiveLayer}
        showExtracts={showExtracts}
        onShowExtractsChange={setShowExtracts}
        showSpawns={showSpawns}
        onShowSpawnsChange={setShowSpawns}
        factions={factions}
        onFactionsChange={setFactions}
        session={session}
        selectedId={selectedId}
        onSelectPin={handleSelectFromList}
        onLabelPin={(id, label) => room.labelPin(id, label)}
        onRemovePin={handleRemoveFromList}
      />

      <main className="stage">
        {/*
          A tab on the edge the sidebar would be on, so the way back is where
          the thing that left was. Hidden in the compact layout, where the
          sidebar is already an overlay with its own button.
        */}
        <button
          type="button"
          className="sidebar-tab"
          aria-label={sidebarHidden ? t("sidebar.show") : t("sidebar.hide")}
          title={sidebarHidden ? t("sidebar.show") : t("sidebar.hide")}
          aria-expanded={!sidebarHidden}
          onClick={() => {
            used("sidebarHide");
            setSidebarHidden((hidden) => !hidden);
          }}
        >
          {sidebarHidden ? "›" : "‹"}
        </button>

        <MapCanvas
          map={map}
          style={style}
          activeLayer={activeLayer}
          pins={room.pins}
          selfId={room.selfId}
          placing={tool !== null}
          drawing={tool === "draw"}
          erasing={tool === "eraser"}
          penColor={penColor}
          compact={compact}
          showExtracts={showExtracts}
          extractFactions={factions}
          showSpawns={showSpawns}
          focus={focus}
          onPlace={handlePlace}
          onDraw={handleDraw}
          onErase={handleErase}
          onSelectPin={handleSelectPin}
          onMovePin={handleMovePin}
        />

        {room.notice && (
          <div className="toast" key={room.notice.id}>
            {room.notice.text}
          </div>
        )}

        {room.status !== "open" && (
          <div className="toast toast--warning">
            {room.status === "connecting"
              ? t("toast.connecting")
              : room.status === "full"
                ? `${room.statusDetail ?? t("toast.busy")} ${t("toast.retrying")}`
                : room.status === "expired"
                  ? (room.statusDetail ?? t("error.session"))
                  : t("toast.reconnecting")}
          </div>
        )}

        {tool && (
          <div className="hint">
            {tool === "draw"
              ? t("hint.drawing")
              : tool === "eraser"
                ? t("hint.erasing")
                : t("hint.place")}
          </div>
        )}

        {selected && !compact && (
          <PinCard
            pin={selected}
            isMine={selected.authorId === room.selfId}
            mapName={mapTitle(map)}
            onLabel={(label) => room.labelPin(selected.id, label)}
            onRemove={() => {
              room.removePin(selected.id, "card");
              setSelectedId(null);
            }}
            onClose={() => setSelectedId(null)}
          />
        )}

        <Toolbar
          tool={tool}
          onToolChange={setTool}
          selfColor={selfColor}
          penColor={penColor}
          onPenColorChange={setPenChoice}
          onClearMine={() => room.clear("mine")}
        />
      </main>
    </div>
  );
}
